import { useCallback, useEffect, useRef, useState } from "react";
import Markdown from "./Markdown";
import SearchGrounding from "./SearchGrounding";
import "./ReviewChat.css";

// Local id for the assistant bubble that grows while tokens stream in.
const STREAMING_ID = "__streaming__";

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.content : "";
}

async function fetchJson(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers ?? {}),
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

// Reads a text/event-stream body, dispatching each `event:`/`data:` frame.
async function consumeStream(body, handlers) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary;
    while ((boundary = buffer.indexOf("\n\n")) !== -1) {
      dispatchFrame(buffer.slice(0, boundary), handlers);
      buffer = buffer.slice(boundary + 2);
    }
  }
}

function dispatchFrame(frame, { onToken, onDone }) {
  let event = "message";
  let data = "";
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data += line.slice(5).trim();
  }
  if (!data) return;
  const payload = JSON.parse(data);
  if (event === "token") onToken(payload.content);
  else if (event === "done") onDone(payload);
  else if (event === "error") throw new Error(payload.message || "stream error");
}

export default function ReviewChat({
  reviewPath,
  enabled,
  onRegenerate,
  canRegenerate = false,
  regenerating = false,
  readOnly = false,
}) {
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchJson(reviewPath);
      setMessages(data.chat.messages);
    } catch (e) {
      setError(e.message);
    }
  }, [reviewPath]);

  useEffect(() => {
    if (!enabled) return;
    load();
  }, [enabled, load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, streaming]);

  const appendToken = (token) =>
    setMessages((prev) => {
      const bubble = prev.find((m) => m.id === STREAMING_ID);
      if (bubble) {
        return prev.map((m) => (m.id === STREAMING_ID ? { ...m, content: m.content + token } : m));
      }
      return [...prev, { id: STREAMING_ID, role: "assistant", content: token }];
    });

  const replaceStreamingBubble = (message) =>
    setMessages((prev) => {
      const next = prev.filter((m) => m.id !== STREAMING_ID);
      return [...next, message];
    });

  const handleSubmit = async (event) => {
    event.preventDefault();
    // The send button is disabled while streaming or when the draft is blank,
    // so handleSubmit only runs with sendable content.
    const content = draft.trim();
    setDraft("");
    setError(null);
    setStreaming(true);
    setMessages((prev) => [...prev, { id: `local-${Date.now()}`, role: "user", content }]);
    try {
      const response = await fetch(reviewPath, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "text/event-stream",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ message: { content } }),
      });
      if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);
      await consumeStream(response.body, {
        onToken: appendToken,
        onDone: replaceStreamingBubble,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setStreaming(false);
    }
  };

  if (!enabled) return null;

  return (
    <section className="trunfo-chat" aria-label="Conversa com a IA jurídica">
      <header className="trunfo-chat__header">
        <div className="trunfo-chat__avatar" aria-hidden>
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path
              fill="currentColor"
              d="M12 2l1.9 4.8L18.8 8.7l-4.9 1.9L12 15.4l-1.9-4.8L5.2 8.7l4.9-1.9L12 2z"
            />
          </svg>
        </div>
        <div className="trunfo-chat__identity">
          <strong>Ana · IA jurídica</strong>
          <span className="trunfo-chat__role">Especializada em direito do consumidor aéreo</span>
        </div>
        <span className="trunfo-chat__status" aria-label="Online">
          <span className="trunfo-chat__status-dot" /> Online
        </span>
      </header>

      <ol className="trunfo-chat__messages">
        {messages.map((m) => (
          <li
            key={m.id}
            className={`trunfo-chat__row trunfo-chat__row--${m.role === "assistant" ? "ia" : "vc"}`}
          >
            <span className="trunfo-chat__badge" aria-hidden>
              {m.role === "assistant" ? "IA" : "VC"}
            </span>
            {m.role === "assistant" ? (
              <div className="trunfo-chat__bubble trunfo-chat__bubble--md">
                <Markdown content={m.content} />
                <SearchGrounding citations={m.citations} suggestions={m.search_suggestions} />
              </div>
            ) : (
              <p className="trunfo-chat__bubble">{m.content}</p>
            )}
          </li>
        ))}
        {streaming && !messages.some((m) => m.id === STREAMING_ID) ? (
          <li className="trunfo-chat__row trunfo-chat__row--ia">
            <span className="trunfo-chat__badge" aria-hidden>
              IA
            </span>
            <p className="trunfo-chat__bubble trunfo-chat__bubble--pending">
              Analisando
              <span className="trunfo-chat__dots" aria-hidden>
                …
              </span>
            </p>
          </li>
        ) : null}
        {canRegenerate && !streaming && !readOnly && onRegenerate ? (
          <li className="trunfo-chat__row trunfo-chat__row--ia">
            <span className="trunfo-chat__badge" aria-hidden>
              IA
            </span>
            <div className="trunfo-chat__bubble trunfo-chat__bubble--action">
              <p>Posso aplicar as mudanças que combinamos e regerar a carta agora?</p>
              <button
                type="button"
                className="trunfo-chat__action"
                onClick={onRegenerate}
                disabled={regenerating}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                  <path
                    fill="currentColor"
                    d="M12 2l1.9 4.8L18.8 8.7l-4.9 1.9L12 15.4l-1.9-4.8L5.2 8.7l4.9-1.9L12 2z"
                  />
                </svg>
                <span>{regenerating ? "Regerando..." : "Regerar carta"}</span>
              </button>
            </div>
          </li>
        ) : null}
        <li ref={bottomRef} aria-hidden />
      </ol>

      {error ? (
        <p className="trunfo-chat__error" role="alert">
          Não foi possível enviar a mensagem. Tente novamente.
        </p>
      ) : null}

      {readOnly ? (
        <p className="trunfo-chat__readonly" role="note">
          Caso concluído. A conversa com a Ana fica disponível apenas para visualização.
        </p>
      ) : (
        <form className="trunfo-chat__composer" onSubmit={handleSubmit}>
          <label className="trunfo-chat__sr-only" htmlFor="review-draft">
            Sua mensagem
          </label>
          <input
            id="review-draft"
            className="trunfo-chat__input"
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Responda ou faça uma pergunta..."
            disabled={streaming}
          />
          <button
            type="submit"
            className="trunfo-chat__send"
            disabled={streaming || draft.trim().length === 0}
          >
            Enviar
          </button>
        </form>
      )}
    </section>
  );
}
